import React, { useState } from 'react';

// ─── CARE PARTNER EMAIL-FIRST FORM ───────────────────────────────────────────
// Validates Gmail, fetches Google profile, locks name/photo, lets user set ID only.
// Used in both SetupWizard (Step 2) and Settings → Family Setup → Care Partners.

export default function CarePartnerEmailForm({ onAdd, onCancel, existingEmails = [] }) {
  const [stage, setStage] = useState('email'); // 'email' | 'loading' | 'confirm' | 'error'
  const [email, setEmail] = useState('');
  const [emailError, setEmailError] = useState(null);
  const [profile, setProfile] = useState(null);
  const [identifier, setIdentifier] = useState('');
  const [phone, setPhone] = useState('');
  const [saving, setSaving] = useState(false);

  function isGmail(addr) {
    return /^[^\s@]+@gmail\.com$/i.test(addr.trim());
  }

  async function lookupEmail() {
    const trimmed = email.trim().toLowerCase();
    setEmailError(null);

    if (!trimmed) { setEmailError('Please enter an email address.'); return; }

    if (!isGmail(trimmed)) {
      setEmailError(
        'Grove currently requires a Gmail address for Care Partners. ' +
        'If they don\'t have one, they can create a free account at gmail.com.'
      );
      return;
    }

    if (existingEmails.map(e => e.toLowerCase()).includes(trimmed)) {
      setEmailError('This person is already a Care Partner.');
      return;
    }

    setStage('loading');

    try {
      const result = await window.googleProfile.fetchByEmail(trimmed);
      const fetchedName  = result?.name      || null;
      const fetchedFirst = result?.givenName  || fetchedName?.split(' ')[0] || null;
      const fetchedPhoto = result?.photo      || null;

      setProfile({
        email: trimmed,
        name:  fetchedName  || trimmed.split('@')[0],
        givenName: fetchedFirst || trimmed.split('@')[0],
        photo: fetchedPhoto,
        fromContacts: !!fetchedName,
      });
      setIdentifier((fetchedFirst || trimmed.split('@')[0])[0].toUpperCase());
      setStage('confirm');
    } catch (err) {
      console.error('Profile fetch failed:', err);
      // Still proceed — we just won't have a name/photo
      const fallbackFirst = trimmed.split('@')[0];
      setProfile({ email: trimmed, name: fallbackFirst, givenName: fallbackFirst, photo: null, fromContacts: false });
      setIdentifier(fallbackFirst[0].toUpperCase());
      setStage('confirm');
    }
  }

  async function confirmAdd() {
    if (!profile || !identifier.trim()) return;
    setSaving(true);
    await onAdd({
      email: profile.email,
      name:  profile.name,
      givenName: profile.givenName,
      photo: profile.photo,
      identifier: identifier.trim().toUpperCase(),
      phone: phone.trim(),
    });
    setSaving(false);
  }

  // ── Stage: email entry ───────────────────────────────────────────────────

  if (stage === 'email') return (
    <div className="cp-email-form">
      <label className="cp-email-form__label">Care Partner's Gmail address</label>
      <div className="cp-email-form__row">
        <input
          className={`input ${emailError ? 'input--error' : ''}`}
          type="email"
          placeholder="name@gmail.com"
          value={email}
          autoFocus
          onChange={e => { setEmail(e.target.value); setEmailError(null); }}
          onKeyDown={e => e.key === 'Enter' && lookupEmail()}
        />
        <button className="btn btn--primary" onClick={lookupEmail} disabled={!email.trim()}>
          Look up →
        </button>
      </div>
      {emailError && (
        <div className="cp-email-form__error">
          <span>⚠ {emailError}</span>
          {!isGmail(email) && email.includes('@') && (
            <a href="https://accounts.google.com/signup" target="_blank" rel="noreferrer"
              className="cp-email-form__signup-link">
              Create a free Gmail account →
            </a>
          )}
        </div>
      )}
      <button className="btn btn--ghost btn--small" onClick={onCancel} style={{ marginTop: 4 }}>Cancel</button>
    </div>
  );

  // ── Stage: loading ───────────────────────────────────────────────────────

  if (stage === 'loading') return (
    <div className="cp-email-form cp-email-form--loading">
      <div className="loading-spinner" />
      <span>Looking up {email}…</span>
    </div>
  );

  // ── Stage: confirm profile ───────────────────────────────────────────────

  if (stage === 'confirm') return (
    <div className="cp-email-form">
      <div className="cp-profile-card">
        <div className="cp-profile-card__avatar">
          {profile.photo
            ? <img src={profile.photo} alt={profile.name} className="cp-profile-card__photo" />
            : <div className="cp-profile-card__initials">{profile.name[0].toUpperCase()}</div>
          }
        </div>
        <div className="cp-profile-card__info">
          <div className="cp-profile-card__name">
            {profile.name}
            {!profile.fromContacts && <span className="cp-profile-card__note"> · name not found, using email prefix</span>}
          </div>
          <div className="cp-profile-card__email">{profile.email}</div>
        </div>
      </div>

      <div className="cp-id-row">
        <label className="cp-id-row__label">
          Assignee ID
          <span className="cp-id-row__hint">Short code used in the Review & Assign action strip — e.g. K for Katy</span>
        </label>
        <input
          className="input input--id"
          maxLength={4}
          value={identifier}
          autoFocus
          onChange={e => setIdentifier(e.target.value.toUpperCase().replace(/[^A-Z]/g, ''))}
          placeholder="A"
        />
      </div>

      <div className="cp-id-row">
        <label className="cp-id-row__label">
          Phone <span className="cp-edit-form__hint">(optional — lets you text them to request coverage)</span>
        </label>
        <input
          className="input"
          type="tel"
          value={phone}
          onChange={e => setPhone(e.target.value)}
          placeholder="(555) 123-4567"
        />
      </div>

      <div className="cp-email-form__actions">
        <button className="btn btn--primary" onClick={confirmAdd}
          disabled={saving || !identifier.trim()}>
          {saving ? 'Adding…' : '+ Add Care Partner'}
        </button>
        <button className="btn btn--ghost" onClick={() => { setStage('email'); setProfile(null); }}>
          ← Change email
        </button>
      </div>
    </div>
  );

  return null;
}
